import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Mail, Phone, MapPin, CheckCircle2, Send } from "lucide-react";
import { CALENDLY_DEMO_URL } from "@/constants/links";
import interiorImg from "@/assets/interior-inspiration.jpg";
import livingRoomImg from "@/assets/living-room-wall.jpg";
import armchairImg from "@/assets/elegant-armchair.jpg";

const CONTACT_INFO = [
  {
    icon: Mail,
    label: "Email Us",
    value: "hello@neiv.in",
    sub: "We reply within 1 business day",
  },
  {
    icon: Phone,
    label: "Call Us",
    value: "+91 98765 43210",
    sub: "Mon – Sat, 10am – 6pm IST",
  },
  {
    icon: MapPin,
    label: "Our Office",
    value: "Bengaluru, India",
    sub: "Available pan-India",
  },
];

const DEMO_INCLUDES = [
  "Live product visualization walkthrough",
  "QR code setup for your showroom",
  "Sales team onboarding overview",
  "Pricing & credit packages tailored to you",
];

export default function ContactUs() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Message from ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.open(`mailto:support@neivai.ai?subject=${subject}&body=${body}`);
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-[#EFEAE4] text-[#2B1B14]">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#EFEAE4]/90 backdrop-blur border-b border-[#947E64]/20">
        <nav className="mx-auto max-w-6xl px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif-display text-2xl font-semibold tracking-tight text-[#2B1B14] inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-[#947E64]" />
            NEIV
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-[#624A36]">
            <Link to="/" className="hover:text-[#947E64] transition">Home</Link>
            <a href="/#categories" className="hover:text-[#947E64] transition">Categories</a>
            <a href="/#features" className="hover:text-[#947E64] transition">About</a>
            <a href={CALENDLY_DEMO_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#947E64] transition">Book a Demo</a>
            <Link to="/contact" className="text-[#947E64]">Contact Us</Link>
          </div>
          <Link
            to="/signup"
            className="rounded-lg bg-[#947E64] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#624A36]"
          >
            Get Started
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={interiorImg} alt="Interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#EFEAE4] via-[#EFEAE4]/80 to-[#EFEAE4]/20" />
        <div className="relative mx-auto max-w-6xl px-4 md:px-8 py-24 md:py-36">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#947E64] mb-4">Get in Touch</span>
          <h1 className="font-serif-display text-5xl md:text-6xl font-medium leading-tight tracking-tight text-[#2B1B14] max-w-xl">
            We'd love to<br />hear from you.
          </h1>
          <p className="mt-5 text-lg text-[#624A36] leading-relaxed max-w-md">
            Whether you're a retailer ready to get started, or just curious about NEIV — reach out and we'll get back to you.
          </p>
        </div>
      </section>

      {/* Contact info strip */}
      <section className="bg-[#2B1B14] py-10">
        <div className="mx-auto max-w-6xl px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CONTACT_INFO.map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-[#947E64]/20 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-[#947E64]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#947E64] mb-0.5">{label}</p>
                <p className="text-[#EFEAE4] font-medium">{value}</p>
                <p className="text-xs text-[#EFEAE4]/50 mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main content — Book a Demo + Contact Form */}
      <section className="mx-auto max-w-6xl px-4 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

        {/* Left — Book a Demo */}
        <div>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#947E64] mb-4">Book a Demo</span>
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium leading-tight tracking-tight text-[#2B1B14]">
            See NEIV in action — live, for your store.
          </h2>
          <p className="mt-4 text-[#624A36] leading-relaxed">
            We'll walk you through how NEIV helps your customers visualize furniture in their own rooms — boosting confidence and closing more sales.
          </p>

          <ul className="mt-8 space-y-3">
            {DEMO_INCLUDES.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-[#624A36]">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#947E64]" />
                {item}
              </li>
            ))}
          </ul>

          <a
            href={CALENDLY_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 rounded-lg bg-[#947E64] px-7 py-3.5 font-medium text-white transition hover:bg-[#624A36]"
          >
            <CalendarDays className="h-4 w-4" /> Schedule a Demo
          </a>

          {/* Images */}
          <div className="mt-10 grid grid-cols-2 gap-4">
            <img src={livingRoomImg} alt="Living room" className="rounded-xl h-44 w-full object-cover shadow-md" />
            <img src={armchairImg} alt="Armchair" className="rounded-xl h-44 w-full object-cover shadow-md" />
          </div>
        </div>

        {/* Right — Contact Form */}
        <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(43,27,20,0.10)] p-8 md:p-10">
          {sent ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-16 w-16 rounded-full bg-[#947E64]/10 flex items-center justify-center mb-5">
                <Send className="h-7 w-7 text-[#947E64]" />
              </div>
              <h3 className="font-serif-display text-2xl font-semibold text-[#2B1B14]">Message sent!</h3>
              <p className="mt-2 text-sm text-[#624A36] max-w-xs leading-relaxed">
                Thanks for reaching out. We'll get back to you within 1 business day.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }}
                className="mt-6 inline-flex items-center gap-2 rounded-lg border border-[#947E64] px-6 py-2.5 text-sm font-medium text-[#2B1B14] hover:bg-[#947E64] hover:text-white transition"
              >
                Send another
              </button>
            </div>
          ) : (
            <>
              <h3 className="font-serif-display text-2xl font-semibold text-[#2B1B14] mb-6">Write to Us</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#947E64] mb-1.5">Your Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Riya Sharma"
                    className="w-full border border-[#E5DDD5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#947E64]/30 focus:border-[#947E64] transition bg-[#FAFAF8]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#947E64] mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="riya@casaboutique.com"
                    className="w-full border border-[#E5DDD5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#947E64]/30 focus:border-[#947E64] transition bg-[#FAFAF8]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#947E64] mb-1.5">Message</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your store or what you'd like to know…"
                    className="w-full border border-[#E5DDD5] rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#947E64]/30 focus:border-[#947E64] transition bg-[#FAFAF8]"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#947E64] px-6 py-3.5 font-medium text-white transition hover:bg-[#624A36]"
                >
                  <Send className="h-4 w-4" /> Send Message
                </button>
              </form>
              <p className="mt-4 text-center text-xs text-[#B7A99A]">We typically respond within 1 business day.</p>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2B1B14] border-t border-[#624A36] py-12 text-[#EFEAE4]/70">
        <div className="mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-serif-display text-2xl font-semibold tracking-tight text-[#EFEAE4] inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-[#947E64]" />
            NEIV
          </Link>
          <div className="flex gap-8 text-sm">
            <Link to="/login" className="transition hover:text-[#947E64]">Retailer Login</Link>
            <Link to="/signup" className="transition hover:text-[#947E64]">Sign up</Link>
          </div>
          <p className="text-xs">© {new Date().getFullYear()} NEIV. Built for interiors.</p>
        </div>
      </footer>
    </div>
  );
}
